import { useMatchRoute, useNavigate } from '@tanstack/react-router'
import { useSelectedOrgAndSiteInterface } from 'features/orgAndSiteSourceOfTruth'
import { useGeckoPermissions } from 'features/permissions'
import { useRightDrawer } from 'features/rightDrawer'
import { useMemo } from 'react'
import {
  SEARCH_PARAM_ORG_ID,
  SEARCH_PARAM_SITE_ID,
} from 'routes/(global_search_params)/-searchParams'
import { createLibraSearch } from 'routes/libra/-searchParams'
import { editOrganizationSearch } from 'routes/organization/-searchParams'
import { createProductionDashboardSearch } from 'routes/production-dashboard/-searchParams'
import { createUsersSearch } from 'routes/users/-searchParams'
import { createWalkdownsSearch } from 'routes/walkdowns/-searchParams'
import {
  GeckoOrganization,
  GeckoSite,
  getOrgAdminPermission,
  PERMISSION_CANTILEVER_CORE_ADMIN,
  PERMISSION_GECKO_INTERNAL_MEMBER,
  RolodexEntryType,
} from 'types'
import { groupBy } from 'utils/fde/maps'

import { OrganizationWithSites, SiteSelectorsData } from './siteSelectorsTypes'

export const useSiteSelectorsState = ({
  organizations,
  selectedOrganization,
  sites,
}: SiteSelectorsData) => {
  const navigate = useNavigate()
  const matchRoute = useMatchRoute()
  const { hasPermissions } = useGeckoPermissions()
  const { setOrgAndSiteIds } = useSelectedOrgAndSiteInterface()
  const { setDrawerState, setDrawerEntry } = useRightDrawer()

  const sitesByOrgId = useMemo(() => {
    return groupBy({
      arr: sites,
      key: 'tags.organization',
    })
  }, [sites])

  const orgsWithAssociatedSites = useMemo(() => {
    return organizations.toSorted(GeckoOrganization.DefaultSort).map((o) => ({
      organization: o,
      sites: sitesByOrgId.get(o.id) ?? [],
    }))
  }, [organizations, sitesByOrgId])

  const sitesFilteredByOrganization = useMemo(
    () =>
      selectedOrganization
        ? (sitesByOrgId.get(selectedOrganization.id) ?? []).toSorted(
            GeckoSite.DefaultSort
          )
        : [],
    [selectedOrganization, sitesByOrgId]
  )

  const handleSiteSelect = async (site: GeckoSite | undefined) => {
    if (!site) return
    await setOrgAndSiteIds(
      {
        organizationId: site.tags[RolodexEntryType.ORGANIZATION],
        siteId: site.id,
      },
      false
    )

    if (
      matchRoute({ to: '/assets' }) ||
      matchRoute({ to: '/assets/unit' }) ||
      matchRoute({ to: '/assets/{-$siteID}' }) ||
      matchRoute({ to: '/assets/$siteID/$unitID' })
    ) {
      navigate({
        to: `/assets`,
        replace: true,
      })
    } else if (matchRoute({ to: '/details/$entryID' })) {
      navigate({
        to: `/assets`,
        replace: true,
      })
    } else if (
      matchRoute({ to: '/health-check' }) ||
      matchRoute({ to: '/admin/users' })
    ) {
      navigate({
        to: `/assets`,
        replace: true,
      })
    } else if (matchRoute({ to: '/viewer/$unitID/configuration' })) {
      navigate({
        to: `/assets`,
        replace: true,
      })
    } else if (matchRoute({ to: '/walkdowns/details' })) {
      navigate({
        to: '/walkdowns',
        search: createWalkdownsSearch(site.id),
        replace: true,
      })
    } else if (matchRoute({ to: '/walkdowns' })) {
      navigate({
        to: '/walkdowns',
        search: createWalkdownsSearch(site.id),
        replace: true,
      })
    } else if (matchRoute({ to: '/explorer/$orgOrSiteID' })) {
      navigate({
        to: `/explorer/${site.id}`,
      })
    } else if (matchRoute({ to: '/inspection-dashboard/$orgOrSiteID' })) {
      navigate({
        to: `/inspection-dashboard/${site.id}`,
      })
    } else if (matchRoute({ to: '/inspection-dashboard' })) {
      navigate({
        to: `/inspection-dashboard/${site.id}`,
      })
    } else if (matchRoute({ to: '/explorer' })) {
      navigate({
        to: `/explorer/${site.id}`,
      })
    } else if (matchRoute({ to: '/production-dashboard' })) {
      navigate({
        to: '/production-dashboard',
        search: createProductionDashboardSearch({
          orgId: site.tags[RolodexEntryType.ORGANIZATION],
          siteId: site.id,
        }),
      })
    } else {
      navigate({
        to: '.',
        params: { siteID: site.id },
      })
    }
  }

  const handleOrgSelect = async (item: OrganizationWithSites | undefined) => {
    const site = item?.sites?.at(0)
    const organization = item?.organization

    await setOrgAndSiteIds(
      { organizationId: organization?.id, siteId: site?.id },
      false
    )

    if (organization?.id && matchRoute({ to: '/users' })) {
      navigate({
        to: '/users',
        search: createUsersSearch({
          orgId: organization.id,
          siteId: site?.id,
        }),
        replace: true,
      })
    } else if (matchRoute({ to: '/extensions' })) {
      navigate({
        to: '/extensions',
        search: {
          [SEARCH_PARAM_ORG_ID]: organization?.id,
          [SEARCH_PARAM_SITE_ID]: site?.id,
        },
        replace: true,
      })
    } else if (matchRoute({ to: '/libra' })) {
      navigate({
        to: '/libra',
        search: createLibraSearch({
          orgId: organization?.id,
          workflowId: undefined,
          ticket: undefined,
          thread: undefined,
        }),
        replace: true,
      })
    } else if (matchRoute({ to: '/explorer/$orgOrSiteID' })) {
      navigate({
        to: `/explorer/${organization?.id}`,
      })
    } else if (matchRoute({ to: '/inspection-dashboard/$orgOrSiteID' })) {
      navigate({
        to: `/inspection-dashboard/${organization?.id}`,
      })
    } else if (matchRoute({ to: '/inspection-dashboard' })) {
      navigate({
        to: `/inspection-dashboard/${organization?.id}`,
      })
    } else if (matchRoute({ to: '/explorer' })) {
      navigate({
        to: `/explorer/${organization?.id}`,
      })
    } else if (matchRoute({ to: '/production-dashboard' })) {
      navigate({
        to: '/production-dashboard',
        search: createProductionDashboardSearch({
          orgId: organization?.id ?? '',
          siteId: site?.id ?? '',
        }),
      })
    } else if (site) {
      handleSiteSelect(site)
    } else if (!site && organization?.id) {
      navigate({
        to: '/organization',
        search: editOrganizationSearch(organization.id),
        replace: true,
      })
    } else {
      navigate({
        to: '.',
        params: { orgID: organization?.id },
        replace: true,
      })
    }
  }

  const handleEmbeddedOrgSelect = async (
    item: OrganizationWithSites | undefined
  ) => {
    const site = item?.sites?.at(0)
    const organization = item?.organization

    await setOrgAndSiteIds(
      { organizationId: organization?.id, siteId: site?.id },
      false
    )

    if (site) {
      await handleSiteSelect(site)
    }
  }

  const canEditOrganizations =
    hasPermissions([PERMISSION_GECKO_INTERNAL_MEMBER]) &&
    hasPermissions([PERMISSION_CANTILEVER_CORE_ADMIN])
  const canEditSites =
    hasPermissions([PERMISSION_GECKO_INTERNAL_MEMBER]) &&
    hasPermissions([
      PERMISSION_CANTILEVER_CORE_ADMIN,
      getOrgAdminPermission(selectedOrganization?.id ?? ''),
    ])

  return {
    navigate,
    setOrgAndSiteIds,
    setDrawerState,
    setDrawerEntry,
    orgsWithAssociatedSites,
    sitesFilteredByOrganization,
    handleSiteSelect,
    handleOrgSelect,
    handleEmbeddedOrgSelect,
    canEditOrganizations,
    canEditSites,
  }
}

export type SiteSelectorsState = ReturnType<typeof useSiteSelectorsState>
